import { useAgent } from "agents/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { BandMemberAvatars } from "./components/BandMemberAvatars";
import { HeroBackdrop } from "./components/HeroBackdrop";
import { CommandHistory } from "./components/CommandHistory";
import { MemoryTimeline } from "./components/MemoryTimeline";
import { MusicTransport } from "./components/MusicTransport";
import { SoundtrackList } from "./components/SoundtrackList";
import { Waveform } from "./components/Waveform";
import type {
  CurrentMixInfo,
  JamRoomStateShape,
  SoundtrackListEntry,
  VoiceCommandResult,
} from "./types";
import "./styles.css";

function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function getRoomFromLocation(): string {
  const q = new URLSearchParams(window.location.search).get("room");
  return q && q.length > 2 ? q : "jam-default";
}

function EternalJamApp() {
  const [roomId, setRoomId] = useState<string>(() => getRoomFromLocation());
  const [roomState, setRoomState] = useState<JamRoomStateShape | null>(null);
  const [textCmd, setTextCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [shareUrl, setShareUrl] = useState(() => window.location.href);
  const [liveAnalyser, setLiveAnalyser] = useState<AnalyserNode | null>(null);
  const [playbackAnalyser, setPlaybackAnalyser] = useState<AnalyserNode | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [soundtracks, setSoundtracks] = useState<SoundtrackListEntry[]>([]);
  const [playingMixVersion, setPlayingMixVersion] = useState<number | null>(
    null,
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const vocalAudioRef = useRef<HTMLAudioElement | null>(null);
  const cloneSampleInputRef = useRef<HTMLInputElement | null>(null);
  /** Mic capture graph (closed after each voice command). */
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  /** Separate graph for `<audio>` playback so the visualizer follows the mix. */
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const playbackAnalyserRef = useRef<AnalyserNode | null>(null);
  const playbackElementWiredRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const textInputRef = useRef<HTMLInputElement | null>(null);

  const connected = roomState !== null;
  const generating =
    roomState?.generationPhase === "directing" ||
    roomState?.generationPhase === "generating";

  const agent = useAgent<JamRoomStateShape>({
    agent: "JamRoom",
    name: roomId,
    onStateUpdate: (s) => setRoomState(s),
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set("room", roomId);
    const next = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", next);
    setShareUrl(window.location.href);
  }, [roomId]);

  /** Route the hidden `<audio>` element through Web Audio so we can analyse it. */
  const ensurePlaybackAnalyser = useCallback(async (el: HTMLAudioElement) => {
    const ctx =
      playbackCtxRef.current ??
      new AudioContext({ latencyHint: "interactive" });
    playbackCtxRef.current = ctx;
    await ctx.resume().catch(() => undefined);

    let an = playbackAnalyserRef.current;
    if (!an) {
      an = ctx.createAnalyser();
      an.fftSize = 256;
      an.smoothingTimeConstant = 0.72;
      playbackAnalyserRef.current = an;
    }

    if (!playbackElementWiredRef.current) {
      try {
        const src = ctx.createMediaElementSource(el);
        src.connect(an);
        an.connect(ctx.destination);
        playbackElementWiredRef.current = true;
      } catch {
        /* Same <audio> may already be wired (Strict Mode / hot reload). */
        playbackElementWiredRef.current = true;
      }
    }

    setPlaybackAnalyser(an);
  }, []);

  const playMixByVersion = useCallback(
    async (mixVersion: number) => {
      if (!roomId || mixVersion < 1) return;
      setStatus("Loading mix…");
      try {
        const info = (await agent.call("getRoomMix", [
          mixVersion,
        ])) as CurrentMixInfo;
        const el = audioRef.current;
        if (!el) return;
        el.pause();
        const prev = el.src;
        if (prev.startsWith("blob:")) URL.revokeObjectURL(prev);
        if (info.audioBase64) {
          const blob = base64ToBlob(info.audioBase64, info.mime);
          el.src = URL.createObjectURL(blob);
        } else {
          el.src = info.audioUrlPath;
        }

        const vel = vocalAudioRef.current;
        if (vel) {
          vel.pause();
          const prevV = vel.src;
          if (prevV.startsWith("blob:")) URL.revokeObjectURL(prevV);
          if (info.vocalOverlayBase64 && info.vocalOverlayMime) {
            const vblob = base64ToBlob(
              info.vocalOverlayBase64,
              info.vocalOverlayMime,
            );
            vel.src = URL.createObjectURL(vblob);
            vel.volume = 0.88;
          } else if (info.vocalOverlayUrlPath) {
            vel.src = info.vocalOverlayUrlPath;
            vel.volume = 0.88;
          } else {
            vel.removeAttribute("src");
            void vel.load();
          }
        }

        setPlayingMixVersion(mixVersion);
        await ensurePlaybackAnalyser(el);
        await el.play();
        if (vel?.src) {
          vel.currentTime = el.currentTime;
          void vel.play().catch(() => undefined);
        }
        setStatus("Playing");
      } catch (e) {
        console.error(e);
        setStatus("Could not play that mix");
      }
    },
    [roomId, agent, ensurePlaybackAnalyser],
  );

  const playLatestMix = useCallback(async () => {
    const v = roomState?.mixVersion;
    if (!v) return;
    await playMixByVersion(v);
  }, [roomState?.mixVersion, playMixByVersion]);

  const refreshSoundtracks = useCallback(async () => {
    await agent.ready;
    try {
      const list = (await agent.call("listSoundtracks", [])) as
        | SoundtrackListEntry[]
        | null;
      setSoundtracks(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
    }
  }, [agent]);

  useEffect(() => {
    setSoundtracks([]);
    setPlayingMixVersion(null);
  }, [roomId]);

  useEffect(() => {
    void refreshSoundtracks();
  }, [roomId, roomState?.mixVersion, refreshSoundtracks]);

  useEffect(() => {
    if (!roomState?.mixVersion) return;
    void playMixByVersion(roomState.mixVersion);
  }, [roomState?.mixVersion, playMixByVersion]);

  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el?.src.startsWith("blob:")) URL.revokeObjectURL(el.src);
      const v = vocalAudioRef.current;
      if (v?.src.startsWith("blob:")) URL.revokeObjectURL(v.src);
    };
  }, []);

  const uploadVoiceClone = async (file: File) => {
    if (!roomId) return;
    setBusy(true);
    setStatus("Uploading voice sample for clone…");
    try {
      const fd = new FormData();
      fd.set("sample", file, file.name || "voice-sample.webm");
      const res = await fetch(
        `/api/jam/${encodeURIComponent(roomId)}/clone-voice`,
        { method: "POST", body: fd },
      );
      const data = (await res.json()) as {
        ok?: boolean;
        error?: string;
        requiresVerification?: boolean;
      };
      if (!res.ok) {
        setStatus(data.error ?? "Voice clone failed");
        return;
      }
      setStatus(
        data.requiresVerification
          ? "Clone created — complete verification in ElevenLabs if prompted."
          : "Your voice is linked. Ask for singing or rap on the next generation.",
      );
    } catch (e) {
      console.error(e);
      setStatus("Voice clone request failed");
    } finally {
      setBusy(false);
      if (cloneSampleInputRef.current) cloneSampleInputRef.current.value = "";
    }
  };

  const clearVoiceClone = async () => {
    setBusy(true);
    await agent.ready;
    await agent.call("clearClonedSingerVoice", []);
    setBusy(false);
    setStatus("Cleared cloned singer voice");
  };

  const ensureMicGraph = useCallback(async () => {
    if (analyserRef.current) return analyserRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 256;
    src.connect(an);
    analyserRef.current = an;
    setLiveAnalyser(an);
    return an;
  }, []);

  const stopMic = useCallback(() => {
    try {
      recorderRef.current?.stop();
    } catch {
      /* ignore */
    }
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;
    analyserRef.current = null;
    setLiveAnalyser(null);
  }, []);

  useEffect(() => () => stopMic(), [stopMic]);

  const startRecording = useCallback(async () => {
    if (recording || busy) return;
    setStatus("Listening… (release to send)");
    await ensureMicGraph();
    const stream = mediaStreamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
    recorderRef.current = rec;
    rec.ondataavailable = (ev) => {
      if (ev.data.size) chunksRef.current.push(ev.data);
    };
    rec.start();
    setRecording(true);
  }, [recording, busy, ensureMicGraph]);

  const stopRecordingAndSend = useCallback(async () => {
    if (!recording) return;
    const rec = recorderRef.current;
    if (!rec) return;
    setRecording(false);
    await new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
      rec.stop();
    });
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];
    setStatus("Transcribing…");
    const fd = new FormData();
    fd.set("audio", blob, "voice.webm");
    const res = await fetch(`/api/jam/${encodeURIComponent(roomId)}/transcribe`, {
      method: "POST",
      body: fd,
    });
    const data = (await res.json()) as { text?: string; error?: string };
    if (!res.ok || !data.text) {
      setStatus(data.error ?? "Transcription failed");
      return;
    }
    setBusy(true);
    setStatus("Directing the band…");
    await agent.ready;
    const r = (await agent.call("voiceCommand", [
      data.text,
      "voice",
    ])) as VoiceCommandResult;
    setBusy(false);
    if (!r.ok) setStatus(r.error ?? "Command failed");
    else setStatus("Got it.");
    stopMic();
  }, [recording, roomId, agent, stopMic]);

  // Spacebar push-to-talk (only when not focused on a text input)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      e.preventDefault();
      void startRecording();
    };
    const up = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      e.preventDefault();
      void stopRecordingAndSend();
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [startRecording, stopRecordingAndSend]);

  const sendText = async () => {
    const t = textCmd.trim();
    if (!t) return;
    setBusy(true);
    setStatus("Sending…");
    await agent.ready;
    const r = (await agent.call("voiceCommand", [
      t,
      "text",
    ])) as VoiceCommandResult;
    setBusy(false);
    setTextCmd("");
    if (!r.ok) setStatus(r.error ?? "Failed");
    else setStatus("Queued generation");
  };

  const createRoom = async () => {
    const res = await fetch("/api/jam/rooms", { method: "POST" });
    const data = (await res.json()) as { id?: string };
    if (data.id) setRoomId(data.id);
  };

  const runDemo = async (id: string) => {
    setBusy(true);
    setStatus("Demo style…");
    await agent.ready;
    const r = (await agent.call("loadDemoStyle", [id])) as {
      ok: boolean;
      error?: string;
    };
    setBusy(false);
    setStatus(r.ok ? "Demo rolling" : r.error ?? "Demo failed");
  };

  const exportTrack = async () => {
    setBusy(true);
    await agent.ready;
    const r = (await agent.call("exportTrack", [])) as {
      ok: boolean;
      r2Key?: string;
      error?: string;
    };
    setBusy(false);
    setStatus(
      r.ok
        ? `Exported${r.r2Key ? ` → ${r.r2Key}` : ""}`
        : r.error ?? "Export failed",
    );
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const moodLine = useMemo(() => {
    if (!roomState) return "Connecting to jam room…";
    const { mood, generationPhase, lastError } = roomState;
    return `${mood.genre} · ${mood.bpm} BPM · ${mood.emotion} · ${generationPhase}${lastError ? ` · ${lastError}` : ""}`;
  }, [roomState]);

  return (
    <div className="app">
      <header className="hero">
        <div className="hero-main">
          <HeroBackdrop />
          <div className="hero-text">
            <h1>Eternal Jam Session</h1>
            <p className="tag">
              Voice-direct an AI band. The room remembers every prompt, evolves
              while you are away, and syncs in realtime across collaborators.
            </p>
          </div>
        </div>
        <div className="pill-row">
          <span className={`conn-dot ${connected ? "connected" : ""}`} />
          <span className="pill">{connected ? `Room: ${roomId}` : "Connecting…"}</span>
          <span className="pill">Cloudflare Agents + DO</span>
          <span className="pill">ElevenLabs Music · STT · SFX</span>
          {roomState?.clonedSingerVoiceId ? (
            <span className="pill pill--clone">Your voice · singer</span>
          ) : null}
        </div>
      </header>

      <div className="main-with-sidebar">
        <SoundtrackList
          tracks={soundtracks}
          activeMixVersion={playingMixVersion}
          disabled={busy}
          onSelect={(v) => void playMixByVersion(v)}
        />
        <div className="main-column">
          <section className={`panel ${generating ? "panel-generating" : ""}`}>
            <Waveform
              analyser={recording ? liveAnalyser : playbackAnalyser}
            />
            <MusicTransport audioRef={audioRef} vocalRef={vocalAudioRef} />
            {roomState && (
              <BandMemberAvatars
                stems={roomState.activeStems}
                phase={roomState.generationPhase}
              />
            )}

            {generating && (
              <div className="gen-bar">
                <div className="gen-bar-fill" />
                <span className="gen-bar-label">
                  {roomState?.generationPhase === "directing"
                    ? "AI is planning the next section…"
                    : "Generating music…"}
                </span>
              </div>
            )}

            <div className="panel-meta">
              <p className="status-line">{moodLine}</p>
              {roomState?.lastError ? (
                <p className="error">{roomState.lastError}</p>
              ) : null}
            </div>

            <div className="control-suite">
              <div className="controls">
                <input
                  ref={textInputRef}
                  type="text"
                  className="control-input"
                  placeholder='Try: "make the sax argue with the drums"'
                  value={textCmd}
                  onChange={(e) => setTextCmd(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !busy && void sendText()}
                  disabled={busy}
                  maxLength={500}
                />
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy || !textCmd.trim()}
                  onClick={() => void sendText()}
                >
                  Send
                </button>
                <button
                  type="button"
                  className={`btn-mic ${recording ? "recording" : ""}`}
                  disabled={busy}
                  onMouseDown={() => void startRecording()}
                  onMouseUp={() => void stopRecordingAndSend()}
                  onMouseLeave={() => recording && void stopRecordingAndSend()}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    void startRecording();
                  }}
                  onTouchEnd={() => void stopRecordingAndSend()}
                  aria-label="Hold to talk — or press spacebar"
                >
                  {recording ? "Release to send" : "Hold to talk"}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busy}
                  onClick={() => void playLatestMix()}
                >
                  Play last mix
                </button>
              </div>

              <div className="share-field">
                <button type="button" className="btn-ghost" onClick={() => void createRoom()}>
                  New room
                </button>
                <input readOnly value={shareUrl} aria-label="Share link" />
                <button type="button" className="btn-ghost" onClick={copyShareLink}>
                  {copied ? "Copied!" : "Copy link"}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busy}
                  onClick={() => void exportTrack()}
                >
                  Export
                </button>
              </div>

          <div className="clone-voice-row">
            <input
              ref={cloneSampleInputRef}
              type="file"
              accept="audio/*,.webm,.wav,.mp3,.m4a"
              className="visually-hidden"
              aria-label="Voice sample for clone"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadVoiceClone(f);
              }}
            />
            <button
              type="button"
              className="btn-ghost"
              disabled={busy}
              onClick={() => cloneSampleInputRef.current?.click()}
            >
              Upload voice (clone for singing)
            </button>
            {roomState?.clonedSingerVoiceId ? (
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => void clearVoiceClone()}
              >
                Clear cloned voice
              </button>
            ) : null}
          </div>
          <p className="hint muted clone-voice-hint">
            Use a clear 30s–2min sample (speech or singing). With a clone, vocal
            requests use your timbre via TTS over an instrumental bed (ElevenLabs
            Music does not accept a voice id directly).
          </p>

          <div className="demo-row">
            <span className="muted demo-label">Demo styles</span>
                {(["lofi-sunday", "neon-night", "soul-basement"] as const).map((id) => (
                  <button
                    key={id}
                    type="button"
                    className="btn-ghost btn-ghost--compact"
                    disabled={busy}
                    onClick={() => void runDemo(id)}
                  >
                    {id.replace(/-/g, " ")}
                  </button>
                ))}
              </div>

              {status ? <p className="status-line status-line--suite">{status}</p> : null}

              <p className="hint muted">
                Spacebar works as push-to-talk when focus is outside the text input.
              </p>
            </div>
          </section>

          <div className="grid-2">
            <div className="panel">
              <CommandHistory timeline={roomState?.timeline ?? []} />
            </div>
            <div className="panel">
              <MemoryTimeline timeline={roomState?.timeline ?? []} />
            </div>
          </div>
        </div>
      </div>

      <audio ref={audioRef} className="visually-hidden" controls={false} />
      <audio ref={vocalAudioRef} className="visually-hidden" controls={false} />
    </div>
  );
}

const root = document.getElementById("root");
if (root) createRoot(root).render(<EternalJamApp />);
